import { INVESTIGATION_HISTORY_PANEL_FETCH_NEXT_PAGE_THRESHOLD_PX } from "#src/routes/investigations/history/consts";
import type { useInvestigationsInfiniteList } from "#src/components/hooks/adapters/useInvestigations";
import { InvestigationHistoryCard } from "#src/routes/investigations/history/InvestigationHistoryCard";
import { InvestigationHistorySectionHeader } from "#src/routes/investigations/history/InvestigationHistorySectionHeader";
import { deduplicateInvestigationHistoryEntries } from "#src/routes/investigations/history/deduplicateInvestigationHistoryEntries";
import {
  flattenInvestigationRecencyGroupsToRows,
  getActiveInvestigationHistoryStickyHeaderIndex,
  getInvestigationHistoryStickyHeaderIndexes,
  groupInvestigationsByRecency,
  type InvestigationHistoryListRowType,
} from "#src/routes/investigations/history/groupInvestigationsByRecency";
import {
  getInvestigationHistoryListRowEstimatedSizePx,
  isInvestigationHistoryListRowSectionEnd,
  shouldInvestigationHistoryCardShowBorderBottom,
} from "#src/routes/investigations/history/investigationHistoryListRowSize";
import {
  getInvestigationHistoryListScrollTopForRowOffset,
  getSelectedInvestigationHistoryRowIndex,
  isInvestigationHistoryRowVisibleInScrollport,
} from "#src/routes/investigations/history/investigationHistoryListScroll";
import {
  getInvestigationHistoryListScrollTop,
  setInvestigationHistoryListScrollTop,
} from "#src/routes/investigations/history/investigationHistoryListScrollStore";
import { resolveInvestigationHistoryPanelScrollAreaFromListRoot } from "#src/routes/investigations/history/investigationHistoryPanelScrollLayout";
import type { InvestigationHistoryFiltersType } from "#src/batteries-included-components/FilterAreas/InvestigationHistoryFilterAreas";
import { EmptyState, Icon } from "@validereinc/common-components";
import type { InvestigationType } from "@validereinc/domain";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames/bind";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./InvestigationHistoryList.module.scss";

const cx = classNames.bind(styles);

type VirtualizerRangeType = {
  startIndex: number;
  endIndex: number;
};

const getVisibleVirtualizerRangeIndexes = ({
  startIndex,
  endIndex,
}: VirtualizerRangeType): number[] => {
  const indexes: number[] = [];

  for (let index = startIndex; index <= endIndex; index += 1) {
    indexes.push(index);
  }

  return indexes;
};

/**
 * Stable key for the history status selection. An empty selection and a
 * cleared filter both omit status from the search request.
 */
const getInvestigationHistoryStatusFilterKey = (
  status: InvestigationHistoryFiltersType["status"]
): string => {
  if (!Array.isArray(status) || status.length === 0) {
    return "";
  }

  return [...status].sort().join(",");
};

type InvestigationsInfiniteListQueryType = ReturnType<
  typeof useInvestigationsInfiniteList
>;

type InvestigationHistoryListProps = {
  selectedInvestigationId?: string;
  onRenameRequest: (investigation: InvestigationType) => void;
  onSetArchived: (investigation: InvestigationType, archived: boolean) => void;
  filters?: InvestigationHistoryFiltersType;
  investigationsQuery: InvestigationsInfiniteListQueryType;
};

export const InvestigationHistoryList = ({
  selectedInvestigationId,
  onRenameRequest,
  onSetArchived,
  filters = {},
  investigationsQuery,
}: InvestigationHistoryListProps) => {
  const listContainerRef = useRef<HTMLDivElement>(null);
  const activeStickyHeaderIndexRef = useRef(0);
  const lastAutoScrolledInvestigationIdRef = useRef<string | null>(null);
  const [openActionsMenuInvestigationId, setOpenActionsMenuInvestigationId] =
    useState<string | null>(null);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const {
    data: investigationsData,
    isLoading: isLoadingInvestigations,
    isFetching: isFetchingInvestigations,
    isFetchingNextPage: isFetchingNextInvestigationsPage,
    hasNextPage: hasNextInvestigationsPage,
    fetchNextPage: fetchNextInvestigationsPage,
  } = investigationsQuery;

  const investigations = useMemo(
    () =>
      deduplicateInvestigationHistoryEntries(
        investigationsData?.pages.flatMap((page) => page.data) ?? []
      ),
    [investigationsData]
  );

  const listRows = useMemo(() => {
    const groups = groupInvestigationsByRecency(investigations);
    return flattenInvestigationRecencyGroupsToRows(groups);
  }, [investigations]);

  const stickyHeaderIndexes = useMemo(
    () => getInvestigationHistoryStickyHeaderIndexes(listRows),
    [listRows]
  );

  const showEmptyState =
    !isLoadingInvestigations &&
    !isFetchingInvestigations &&
    investigations.length === 0;

  const handleInvestigationActionsMenuOpenChange = useCallback(
    (investigationId: string, isOpen: boolean) => {
      setOpenActionsMenuInvestigationId((currentOpenInvestigationId) => {
        if (isOpen) {
          return investigationId;
        }

        return currentOpenInvestigationId === investigationId
          ? null
          : currentOpenInvestigationId;
      });
    },
    []
  );

  const handleDismissInvestigationActionsMenu = useCallback(() => {
    setOpenActionsMenuInvestigationId(null);
  }, []);

  useLayoutEffect(() => {
    setScrollElement(
      resolveInvestigationHistoryPanelScrollAreaFromListRoot(
        listContainerRef.current
      )
    );
  }, [listRows.length, isLoadingInvestigations]);

  const investigationHistoryStatusFilterKey =
    getInvestigationHistoryStatusFilterKey(filters.status);
  const previousSearchQueryRef = useRef<string | undefined>();
  /** Empty key matches no status selection, so the first render is not a change. */
  const previousInvestigationHistoryStatusFilterKeyRef = useRef("");

  useEffect(() => {
    const searchQuery = filters.title;
    const hasSearchQueryChanged =
      previousSearchQueryRef.current !== searchQuery;
    const hasStatusFilterChanged =
      previousInvestigationHistoryStatusFilterKeyRef.current !==
      investigationHistoryStatusFilterKey;

    previousSearchQueryRef.current = searchQuery;
    previousInvestigationHistoryStatusFilterKeyRef.current =
      investigationHistoryStatusFilterKey;

    if (!hasSearchQueryChanged && !hasStatusFilterChanged) {
      return;
    }

    if (!scrollElement || typeof scrollElement.scrollTo !== "function") {
      return;
    }

    setInvestigationHistoryListScrollTop(0);
    scrollElement.scrollTo({ top: 0 });
  }, [filters.title, investigationHistoryStatusFilterKey, scrollElement]);

  const estimateRowSize = useCallback(
    (index: number) =>
      getInvestigationHistoryListRowEstimatedSizePx(index, listRows),
    [listRows]
  );

  const getRowKey = useCallback(
    (index: number) => listRows[index]?.id ?? index,
    [listRows]
  );

  const stickyRangeExtractor = useCallback(
    (range: VirtualizerRangeType) => {
      activeStickyHeaderIndexRef.current =
        getActiveInvestigationHistoryStickyHeaderIndex(
          stickyHeaderIndexes,
          range.startIndex
        );

      return [
        ...new Set([
          activeStickyHeaderIndexRef.current,
          ...getVisibleVirtualizerRangeIndexes(range),
        ]),
      ].sort((leftIndex, rightIndex) => leftIndex - rightIndex);
    },
    [stickyHeaderIndexes]
  );

  const isActiveStickyHeaderIndex = useCallback(
    (index: number) => activeStickyHeaderIndexRef.current === index,
    []
  );

  const virtualizer = useVirtualizer({
    count: listRows.length,
    getScrollElement: () => scrollElement,
    estimateSize: estimateRowSize,
    getItemKey: getRowKey,
    overscan: 8,
    rangeExtractor: stickyRangeExtractor,
    initialOffset: selectedInvestigationId
      ? getInvestigationHistoryListScrollTop()
      : 0,
  });

  useLayoutEffect(() => {
    lastAutoScrolledInvestigationIdRef.current = null;
  }, [selectedInvestigationId]);

  useLayoutEffect(() => {
    if (selectedInvestigationId || !scrollElement || isLoadingInvestigations) {
      return;
    }

    setInvestigationHistoryListScrollTop(0);
    virtualizer.scrollToOffset(0, { behavior: "auto" });
  }, [
    isLoadingInvestigations,
    scrollElement,
    selectedInvestigationId,
    virtualizer,
  ]);

  useLayoutEffect(() => {
    if (
      !selectedInvestigationId ||
      !scrollElement ||
      isLoadingInvestigations ||
      showEmptyState
    ) {
      return;
    }

    if (
      lastAutoScrolledInvestigationIdRef.current === selectedInvestigationId
    ) {
      return;
    }

    const selectedRowIndex = getSelectedInvestigationHistoryRowIndex(
      listRows,
      selectedInvestigationId
    );

    if (selectedRowIndex === -1) {
      return;
    }

    const measuredRow = virtualizer
      .getVirtualItems()
      .find((virtualItem) => virtualItem.index === selectedRowIndex);
    const offsetForIndex = virtualizer.getOffsetForIndex(
      selectedRowIndex,
      "start"
    );

    if (!offsetForIndex) {
      return;
    }

    const [rowOffset] = offsetForIndex;
    const rowSize = measuredRow?.size ?? estimateRowSize(selectedRowIndex);
    const isSelectedRowVisible = isInvestigationHistoryRowVisibleInScrollport({
      scrollTop: scrollElement.scrollTop,
      viewportHeight: scrollElement.clientHeight,
      rowOffset,
      rowSize,
    });

    lastAutoScrolledInvestigationIdRef.current = selectedInvestigationId;

    if (!isSelectedRowVisible) {
      const targetScrollTop =
        getInvestigationHistoryListScrollTopForRowOffset(rowOffset);

      virtualizer.scrollToOffset(targetScrollTop, {
        behavior: "auto",
      });
      setInvestigationHistoryListScrollTop(targetScrollTop);
    }
  }, [
    estimateRowSize,
    isLoadingInvestigations,
    listRows,
    scrollElement,
    selectedInvestigationId,
    showEmptyState,
    virtualizer,
  ]);

  const handleScroll = useCallback(() => {
    if (!scrollElement) {
      return;
    }

    setInvestigationHistoryListScrollTop(scrollElement.scrollTop);

    const distanceFromBottom =
      scrollElement.scrollHeight -
      scrollElement.scrollTop -
      scrollElement.clientHeight;

    if (
      distanceFromBottom <
        INVESTIGATION_HISTORY_PANEL_FETCH_NEXT_PAGE_THRESHOLD_PX &&
      hasNextInvestigationsPage &&
      !isFetchingNextInvestigationsPage
    ) {
      void fetchNextInvestigationsPage();
    }
  }, [
    fetchNextInvestigationsPage,
    hasNextInvestigationsPage,
    isFetchingNextInvestigationsPage,
    scrollElement,
  ]);

  useEffect(() => {
    if (!scrollElement) {
      return;
    }

    scrollElement.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, scrollElement]);

  useEffect(() => {
    const virtualItems = virtualizer.getVirtualItems();
    const lastVirtualItem = virtualItems.at(-1);

    if (
      lastVirtualItem &&
      lastVirtualItem.index >= listRows.length - 3 &&
      hasNextInvestigationsPage &&
      !isFetchingNextInvestigationsPage
    ) {
      void fetchNextInvestigationsPage();
    }
  }, [
    fetchNextInvestigationsPage,
    hasNextInvestigationsPage,
    isFetchingNextInvestigationsPage,
    listRows.length,
    virtualizer,
  ]);

  const renderRow = (
    row: InvestigationHistoryListRowType,
    rowIndex: number
  ) => {
    if (row.type === "header") {
      return <InvestigationHistorySectionHeader bucket={row.bucket} />;
    }

    return (
      <InvestigationHistoryCard
        investigation={row.investigation}
        isSelected={row.investigation.id === selectedInvestigationId}
        hasBorderBottom={shouldInvestigationHistoryCardShowBorderBottom(
          rowIndex,
          listRows
        )}
        isActionsMenuOpen={
          openActionsMenuInvestigationId === row.investigation.id
        }
        onActionsMenuOpenChange={(isOpen) =>
          handleInvestigationActionsMenuOpenChange(row.investigation.id, isOpen)
        }
        onDismissActionsMenu={handleDismissInvestigationActionsMenu}
        onRename={onRenameRequest}
        onSetArchived={onSetArchived}
      />
    );
  };

  return (
    <div
      ref={listContainerRef}
      className={cx("list-root", { "list-root--empty": showEmptyState })}
      data-testid="investigation-history-list"
    >
      {showEmptyState ? (
        <EmptyState
          className={cx("empty-state")}
          title="No investigations found"
          icon={<Icon variant="magnifying-glass" />}
        />
      ) : (
        <ol
          className={cx("list")}
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const row = listRows[virtualItem.index];

            if (!row) {
              return null;
            }

            const isActiveStickyHeader = isActiveStickyHeaderIndex(
              virtualItem.index
            );
            const isSectionEndRow = isInvestigationHistoryListRowSectionEnd(
              virtualItem.index,
              listRows
            );

            return (
              <li
                key={row.id}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                className={cx(
                  "list-row",
                  row.type === "header" ? "list-row--header" : "list-row--item",
                  isSectionEndRow && "list-row--section-end",
                  isActiveStickyHeader && "list-row--sticky-active"
                )}
                style={{
                  height: `${virtualItem.size}px`,
                  ...(isActiveStickyHeader
                    ? { top: 0 }
                    : {
                        transform: `translateY(${virtualItem.start}px)`,
                      }),
                }}
              >
                {renderRow(row, virtualItem.index)}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
